import { useCallback, useEffect, useId, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
} from "@mui/material";

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 350;

function unwrapHttp(response) {
  if (
    response &&
    typeof response === "object" &&
    "data" in response &&
    !("meta" in response)
  ) {
    return response.data;
  }
  return response;
}

export default function PaginatedCatalogAutocomplete({
  active,
  label,
  value,
  onChange,
  loadPage,
  normalizeOption,
  getOptionLabel,
  getOptionDescription,
  renderOption,
  disabled = false,
  error = "",
  compact = false,
  required = true,
  placeholder,
}) {
  const fieldId = useId();
  const requestIdRef = useRef(0);
  const [inputValue, setInputValue] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [options, setOptions] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    if (!active) return;
    setInputValue(value ? getOptionLabel(value) : "");
    setSearch("");
    setPage(1);
    setLoadError("");
  }, [active, getOptionLabel, value]);

  useEffect(() => {
    if (!active) return undefined;
    if (value && inputValue === getOptionLabel(value)) {
      if (search) setSearch("");
      return undefined;
    }
    const timeout = setTimeout(() => {
      const normalized = inputValue.trim();
      if (normalized !== search) {
        setOptions([]);
        setPage(1);
        setSearch(normalized);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [active, getOptionLabel, inputValue, search, value]);

  const fetchOptions = useCallback(
    async (signal) => {
      const requestId = ++requestIdRef.current;
      setLoading(true);
      setLoadError("");
      try {
        const response = await loadPage({
          page,
          limit: PAGE_SIZE,
          search: search || undefined,
          signal,
        });
        const payload = unwrapHttp(response);
        if (signal.aborted || requestId !== requestIdRef.current) return;
        const list = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload)
          ? payload
          : [];
        const normalized = list.map(normalizeOption).filter(Boolean);
        setOptions((current) => {
          if (page === 1) return normalized;
          const byId = new Map(current.map((option) => [option.id, option]));
          for (const option of normalized) byId.set(option.id, option);
          return [...byId.values()];
        });
        setTotalPages(
          Number(payload?.meta?.totalPages) || (normalized.length ? 1 : 0)
        );
      } catch (requestError) {
        if (signal.aborted || requestId !== requestIdRef.current) return;
        setOptions([]);
        setTotalPages(0);
        setLoadError(requestError?.message || "No se pudo cargar el catálogo.");
      } finally {
        if (!signal.aborted && requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    }, [loadPage, normalizeOption, page, search]
  );

  useEffect(() => {
    if (!active || disabled) return undefined;
    const controller = new AbortController();
    fetchOptions(controller.signal);
    return () => controller.abort();
  }, [active, disabled, fetchOptions]);

  return (
    <Box>
      <Autocomplete
        id={fieldId}
        fullWidth
        value={value}
        inputValue={inputValue}
        options={options}
        loading={loading}
        disabled={disabled}
        filterOptions={(catalogOptions) => catalogOptions}
        isOptionEqualToValue={(option, selected) => option.id === selected.id}
        getOptionLabel={getOptionLabel}
        onInputChange={(_, nextInput, reason) => {
          if (reason === "input" || reason === "clear") setInputValue(nextInput);
        }}
        onChange={(_, option) => {
          onChange(option);
          setInputValue(option ? getOptionLabel(option) : "");
        }}
        renderOption={renderOption}
        ListboxProps={{
          onScroll: (event) => {
            const listbox = event.currentTarget;
            const nearBottom =
              listbox.scrollTop + listbox.clientHeight >=
              listbox.scrollHeight - 24;
            if (nearBottom && !loading && page < totalPages) {
              setPage((current) => current + 1);
            }
          },
        }}
        noOptionsText={search ? "Sin resultados" : "No hay opciones activas"}
        loadingText="Buscando..."
        renderInput={(params) => (
          <TextField
            {...params}
            required={required}
            label={compact ? undefined : label}
            placeholder={placeholder || `Buscar ${label.toLowerCase()}...`}
            error={Boolean(error || loadError)}
            helperText={
              compact
                ? error || loadError || ""
                : error ||
                  loadError ||
                  (value && getOptionDescription
                    ? getOptionDescription(value)
                    : " ")
            }
            FormHelperTextProps={{
              role: error || loadError ? "alert" : undefined,
              sx: {
                mx: 0.4,
                mt: compact ? 0 : 0.45,
                minHeight: compact ? 0 : 18,
                fontSize: 11.5,
                fontWeight: 600,
                display: compact && !(error || loadError) ? "none" : undefined,
              },
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            inputProps={{
              ...params.inputProps,
              "aria-label": label,
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: compact ? 38 : undefined,
                minHeight: compact ? 38 : 52,
                borderRadius: compact ? "33px" : "16px",
                bgcolor: compact ? "neutral.50" : "logoblanco",
                color: "primary.900",
                fontSize: compact ? 13 : undefined,
                fontWeight: compact ? 600 : undefined,
                boxShadow: compact
                  ? "inset 0px 0px 2px rgba(96, 96, 113, 0.5)"
                  : undefined,
                "& fieldset": compact ? { border: "none" } : undefined,
                "&:hover fieldset": compact ? { border: "none" } : undefined,
              },
              "& .MuiInputBase-input::placeholder": {
                color: "neutral.700",
                opacity: 0.9,
                fontWeight: 600,
              },
            }}
          />
        )}
      />
    </Box>
  );
}
